// src/components/contacts/ContactWorkspaceCallColumn.tsx
//
// CRM call surface — rendered INSIDE the Contact Workspace drawer. This is a
// CRM experience: it reuses Outbound's calling infrastructure but does NOT
// expose Outbound's outcome/disposition workflow.
//
//   Call Details (+ Call button, read-only last outcome/last call)
//   ├─ connecting → amber "Ringing {name}…" card + "Cancel Call"
//   ├─ in_call    → green "Browser Call Active" + timer + stay-on-window
//   │               warning + Mute / Keypad / Hang Up + Speaker/Mic + keypad
//   └─ ended/failed → small "Call ended • MM:SS" line (drawer stays open)
//   Call Notes → ONE standalone notes field (visible before/during/after a
//                call), saved via the existing Add Note endpoint. Not tied to
//                outcome or callback.
//
// NO outcome tiles, NO outcome banner, NO recovery prompt, NO Save Outcome,
// NO callback scheduler. Outbound remains the authority for disposition; the
// CRM simply does not render it.
//
// All call actions (mute / unmute / end / DTMF / mic / speaker) drive the
// SINGLE shared useTwilioVoiceDevice instance passed in as `voice`.

import { useEffect, useRef, useState } from 'react';
import {
  usePrepareOutboundContactCallMutation,
  useAddOutboundContactNoteMutation,
  outboundContactWorkspaceApi,
  OUTBOUND_NOTE_MAX,
} from '../../api/outboundContactWorkspaceApi';
import type { OutboundContactWorkspaceOkResponse } from '../../api/outboundContactWorkspaceApi';
import {
  useTwilioVoiceDevice,
  type UseTwilioVoiceDeviceResult,
} from '../../hooks/useTwilioVoiceDevice';
import { useAppDispatch } from '../../store/store';
// The pre-call OutgoingRecordCallToggle + useOutgoingCallLauncher were
// intentionally removed from this surface. The recording decision is
// now mid-call only, made inside ActiveCallInline via
// useActiveCallRecording. Do not re-add the pre-call widget here.
import { ActiveCallRecordingCard } from '../../telephony/activeCallRecording/ActiveCallRecordingCard';
import CallingProviderSelector, {
  type CallingProviderOption,
} from './CallingProviderSelector';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';
import { useContactPermissions } from '../../hooks/useContactCompanyPermissions';

interface Props {
  crmContactId: string | null;
  workspaceData: OutboundContactWorkspaceOkResponse | undefined;
  workspaceStatus: 'loading' | 'ok' | 'not_synced' | 'unavailable';
  /** The SHARED voice-device instance, owned by the page. */
  voice?: UseTwilioVoiceDeviceResult;
}

function Label({ text }: { text: string }) {
  return <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{text}</p>;
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.max(0, seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
const DTMF_TESTID: Record<string, string> = { '*': 'star', '#': 'hash' };

// Read-only label for a stored outcome enum (Call Details history line only —
// this is historical data, NOT the disposition workflow).
const OUTCOME_LABELS: Record<string, string> = {
  completed: 'Completed',
  no_answer: 'No Answer',
  voicemail: 'Left Voicemail',
  callback: 'Callback Requested',
  not_interested: 'Not Interested',
  wrong_number: 'Wrong Number',
};
function formatOutcomeLabel(outcome: string): string {
  return (
    OUTCOME_LABELS[outcome] ??
    outcome.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function CallButtonView({
  crmContactId,
  contactName,
  contactPhone,
  voice,
}: {
  crmContactId: string;
  contactName: string | null;
  contactPhone: string | null;
  voice: UseTwilioVoiceDeviceResult;
}) {
  const [prepare, prepState] = usePrepareOutboundContactCallMutation();
  const isVoiceReady = voice.isReady;
  const activeKind = voice.activeCall.kind;
  const hasActive = activeKind === 'connecting' || activeKind === 'in_call';
  const isPreparing = prepState.isLoading;

  // Provider-branching. `managedBy: 'outbound'` means Outbound's TwiML app
  // owns the call — CRM must not route via connectToNumber on that path.
  // Defensive on `voice.status` since some legacy callers pass a partial
  // voice mock without it.
  const status = voice.status as
    | { kind?: string; managedBy?: string; recordingMode?: 'off' | 'always' }
    | undefined;
  const managedByOutbound =
    status?.kind === 'ready' && status.managedBy === 'outbound';

  // Pre-call provider selection (Phase 4). Only meaningful on the
  // outbound-managed path (CRM-native calls always dial through the
  // workspace's single Twilio integration — no per-provider choice).
  // The selector remains visually present but is inert / read-only
  // on the CRM-native path so the operator always sees which
  // provider is going to place the call.
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace?.id;
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [selectorReadiness, setSelectorReadiness] = useState<{
    canPlaceCall: boolean;
    reason: string | null;
    selectedOption: CallingProviderOption | null;
  }>({ canPlaceCall: false, reason: 'Loading providers…', selectedOption: null });
  // Local echo-mismatch surface so the standard `feedback` branch below
  // renders it without needing a separate slot.
  const [providerEchoError, setProviderEchoError] = useState<string | null>(
    null,
  );

  let disabledReason: string | null = null;
  if (!isVoiceReady) disabledReason = 'The voice device is not ready yet.';
  else if (hasActive) disabledReason = 'A call is already in progress.';
  else if (isPreparing) disabledReason = 'Preparing your call…';
  else if (managedByOutbound && !selectorReadiness.canPlaceCall) {
    disabledReason =
      selectorReadiness.reason ??
      'Select a calling provider before placing this call.';
  }

  let feedback: { kind: 'error' | 'info'; text: string } | null = null;
  if (prepState.isError && prepState.error) {
    const e = prepState.error as { status?: number; data?: { error?: string; message?: string } };
    const code = e.data?.error;
    if (code === 'CONTACT_NOT_SYNCED') feedback = { kind: 'error', text: 'This contact is not available in Outbound.' };
    else if (code === 'NO_PHONE_NUMBER') feedback = { kind: 'error', text: 'No phone number is available for this contact.' };
    else if (code === 'INVALID_PHONE_NUMBER') feedback = { kind: 'error', text: 'This contact’s phone number is not valid.' };
    else if (code === 'CONTACT_BLOCKED') feedback = { kind: 'error', text: 'Calling is blocked for this contact.' };
    else if (code === 'VOICE_PROVIDER_NOT_READY' || code === 'FROM_NUMBER_NOT_AVAILABLE') feedback = { kind: 'error', text: 'The Outbound voice provider is not ready.' };
    else if (code === 'OUTBOUND_PROVIDER_ID_MISSING') feedback = { kind: 'error', text: 'Outbound did not return the provider identity for this call. Please try again.' };
    else if (code === 'PROVIDER_ECHO_MISMATCH') feedback = { kind: 'error', text: 'The provider Outbound resolved for this call does not match your selection. The call was refused.' };
    else if (e.status === 403) feedback = { kind: 'error', text: 'You are not permitted to place this call.' };
    else feedback = { kind: 'error', text: e.data?.message ?? 'Calling is temporarily unavailable.' };
  }
  // Client-side echo mismatch (defense in depth — the server also
  // fails closed with `PROVIDER_ECHO_MISMATCH`).
  if (providerEchoError) {
    feedback = { kind: 'error', text: providerEchoError };
  }

  const canClick = !disabledReason;
  const label = isPreparing ? 'Preparing…' : 'Call';

  const handleClick = async () => {
    if (!canClick) return;
    setProviderEchoError(null);
    try {
      if (managedByOutbound) {
        // Outbound-managed pipeline. Per-provider CRM calling REQUIRES this
        // strict sequence — any deviation lets a call go out on the wrong
        // Twilio account:
        //
        //   1. prepare(fromNumberId=B)         → stamps Provider B on the
        //                                        OutboundMessage row.
        //   2. verify prepared.workspaceProviderId === B
        //                                     → refuses the call if
        //                                        Outbound resolved
        //                                        something else (e.g., B
        //                                        got suspended between the
        //                                        selector and prepare).
        //   3. voice.switchProvider(B)         → destroys the current
        //                                        Device (bound to A's
        //                                        TwiML App), asks Outbound
        //                                        for a fresh JWT minted
        //                                        with B's Twilio account
        //                                        credentials, re-registers
        //                                        the Device against B's
        //                                        TwiML App.
        //   4. verify switchResult.outboundProviderId === B
        //                                     → server-side + client-side
        //                                        echo — refuses on any
        //                                        mismatch.
        //   5. voice.connectToOutboundCall({callId,toNumber})
        //                                     → the Device is now bound
        //                                        to B, so the browser leg
        //                                        joins B's Twilio account
        //                                        and Outbound voip-connect
        //                                        adds the lead leg via B
        //                                        (using the persisted
        //                                        payload.resolvedProviderId
        //                                        stamped in step 1).
        //
        // No pre-call `record` param is sent to prepare — the recording
        // decision is provider-owned server-side via
        // `payload.recordingPolicy`, which is built from Provider B's
        // Manual/Automatic mode. See buildRecordingSnapshotAtPrep on the
        // Outbound side.
        const fromNumberId = selectedProviderId ?? undefined;
        const res = await prepare({ crmContactId, fromNumberId }).unwrap();
        const p = res.prepared;
        if (
          fromNumberId &&
          p.workspaceProviderId &&
          p.workspaceProviderId !== fromNumberId
        ) {
          setProviderEchoError(
            'The provider Outbound resolved for this call does not match your selection. The call was refused.',
          );
          return;
        }

        // Re-mint + rebuild the Device against the selected provider so
        // the subsequent Device.connect() reaches Provider B's TwiML App
        // instead of the page-load default. Failure is fail-closed:
        // never fall through to connectToOutboundCall on the wrong token.
        if (fromNumberId && voice.switchProvider) {
          const switchResult = await voice.switchProvider(fromNumberId);
          if (!switchResult.ok) {
            const codeMessage: Partial<Record<typeof switchResult.code, string>> = {
              OUTBOUND_PROVIDER_ID_MISSING:
                'The selected provider id was missing. Please pick a provider and try again.',
              TOKEN_PROVIDER_MISMATCH:
                'The Voice token was issued for a different Twilio account than your selection. The call was refused.',
              VOICE_PROVIDER_NOT_FOUND:
                'The selected calling provider no longer exists. Ask an admin to reconfigure the workspace.',
              VOICE_PROVIDER_SUSPENDED:
                'The selected calling provider is suspended. Pick another provider.',
              VOICE_PROVIDER_WORKSPACE_MISMATCH:
                'The selected provider does not belong to this workspace.',
              PROVIDER_NOT_VOICE:
                'The selected provider is not a Voice provider.',
              PROVIDER_VOIP_CONFIG_INCOMPLETE:
                'The selected provider is missing API Key + TwiML App configuration.',
              TWILIO_MANAGED_BY_OUTBOUND_UNAVAILABLE:
                'Outbound could not mint a Voice token for the selected provider right now.',
              DEVICE_REBUILD_FAILED:
                'The Voice device could not be rebuilt for the selected provider. Please try again.',
              TOKEN_ERROR:
                switchResult.message ||
                'Failed to mint a Voice token for the selected provider.',
            };
            setProviderEchoError(
              codeMessage[switchResult.code] ??
                switchResult.message ??
                'Could not switch to the selected calling provider.',
            );
            return;
          }
        }

        await voice.connectToOutboundCall(
          { callId: p.callId, toNumber: p.toNumber },
          { contactId: crmContactId, contactName: contactName ?? undefined },
        );
      } else {
        // CRM-native: dial without a pre-call record decision — the
        // mid-call toggle drives Twilio recording via the REST endpoints
        // after answer.
        if (!contactPhone) {
          return;
        }
        await voice.connectToNumber(contactPhone, {
          contactId: crmContactId,
          contactName: contactName ?? undefined,
        });
      }
    } catch {
      return;
    }
    // Reset is now driven by `activeCallKind` transitioning to a
    // terminal state — no manual reset here. If a synchronous launch
    // failure never touched the call state machine, the toggle stays
    // set so the user can retry with the same preference intact.
  };

  return (
    <>
      {/* Pre-call provider selector (Phase 4). Only rendered on the
          outbound-managed path — for CRM-native calls there is one
          Twilio integration and `voice.connectToNumber` dials it
          directly. Selector reads mirrored provider metadata ONLY;
          picking a provider never creates call/recording rows,
          never touches ownerApp / callOwner / providerOwner /
          storageOwner. */}
      {managedByOutbound && (
        <div className="mb-3" data-testid="cw-calling-provider-slot">
          <CallingProviderSelector
            workspaceId={workspaceId}
            value={selectedProviderId}
            onChange={(id) => {
              setSelectedProviderId(id);
              setProviderEchoError(null);
            }}
            disabled={isPreparing || hasActive}
            onReadinessChange={setSelectorReadiness}
          />
        </div>
      )}
      {/* Pre-call recording toggle intentionally removed — the mid-call
          RecordingControl inside ActiveCallInline is now the single
          source of truth for the recording decision. See
          useActiveCallRecording. */}
      <button
        type="button"
        data-testid="cw-call-button"
        aria-label={disabledReason ?? 'Call'}
        title={disabledReason ?? 'Call'}
        onClick={() => void handleClick()}
        disabled={!canClick}
        className="mt-4 w-full py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isPreparing ? (
          <span aria-hidden className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
        ) : (
          <span aria-hidden>📞</span>
        )}
        {label}
      </button>
      {disabledReason ? (
        <p data-testid="cw-call-button-disabled-reason" className="mt-2 text-[11px] text-slate-500 leading-snug">{disabledReason}</p>
      ) : null}
      {feedback ? (
        <p data-testid="cw-call-feedback" data-kind={feedback.kind} className={feedback.kind === 'error' ? 'mt-2 text-xs text-red-700' : 'mt-2 text-xs text-slate-600'}>
          {feedback.text}
        </p>
      ) : null}
    </>
  );
}

function CallButtonSelfContained({
  crmContactId,
  contactName,
  contactPhone,
}: {
  crmContactId: string;
  contactName: string | null;
  contactPhone: string | null;
}) {
  const dispatch = useAppDispatch();
  const voice = useTwilioVoiceDevice();
  useCallTerminalRefetch(voice.activeCall.kind, crmContactId, dispatch);
  return (
    <CallButtonView
      crmContactId={crmContactId}
      contactName={contactName}
      contactPhone={contactPhone}
      voice={voice}
    />
  );
}

/** Refetches the workspace + live status once the active call ends or fails. */
export function useCallTerminalRefetch(
  activeKind: string,
  crmContactId: string,
  dispatch: ReturnType<typeof useAppDispatch>,
) {
  const lastFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!crmContactId) return;
    const marker = `${activeKind}:${crmContactId}`;
    if (activeKind === 'idle') {
      lastFiredRef.current = null;
      return;
    }
    if (activeKind !== 'ended' && activeKind !== 'failed') return;
    if (lastFiredRef.current === marker) return;
    lastFiredRef.current = marker;
    dispatch(outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(crmContactId, { forceRefetch: true, subscribe: false }));
    dispatch(outboundContactWorkspaceApi.endpoints.getOutboundContactCallStatus.initiate(crmContactId, { forceRefetch: true, subscribe: false }));
  }, [activeKind, crmContactId, dispatch]);
}

// ── Inline in-call controls (Outbound parity: rectangular Mute/Keypad/Hang Up
//    + Speaker/Mic dropdowns + collapsible keypad) ─────────────────────────
function ActiveCallInline({ voice, name }: { voice: UseTwilioVoiceDeviceResult; name: string }) {
  const [showKeypad, setShowKeypad] = useState(false);
  const call = voice.activeCall;
  const muted = call.kind === 'in_call' && call.muted;
  const durationSeconds = call.kind === 'in_call' ? call.durationSeconds : 0;
  // Mid-call recording control inputs. `twilioCallSid` is populated on
  // Twilio SDK 'accept' — before that it's undefined, so the control
  // renders inert (start is a no-op with no SID).
  const twilioCallSid =
    call.kind === 'in_call' ? call.twilioCallSid ?? null : null;
  // Phase 1 linking — read the outboundCallId that was stamped onto
  // the ActiveCall state by `connectToOutboundCall`. Passed to the
  // recording card so the mid-call start endpoint can bind the new
  // CallRecording to its parent placeholder CallSession. Present on
  // both 'connecting' and 'in_call' states for the Outbound-managed
  // branch; absent for CRM-native calls (no-op).
  const outboundCallId =
    call.kind === 'in_call' || call.kind === 'connecting'
      ? (call as { outboundCallId?: string }).outboundCallId ?? null
      : null;
  const status = voice.status as
    | { kind?: string; recordingMode?: 'off' | 'always' }
    | undefined;
  const workspaceRecordingMode: 'off' | 'always' | undefined =
    status?.kind === 'ready' ? status.recordingMode : undefined;
  const audio = voice.audioDevices;
  const showMic = audio.inputs.length >= 1;
  const showSpeaker = audio.outputSelectionSupported && audio.outputs.length >= 1;

  return (
    <section data-testid="cw-call-active" className="space-y-3">
      {/* Green "Browser Call Active" card + timer */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 shadow-sm px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-none" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-900 truncate">{name}</p>
            <p className="text-xs text-emerald-700">Browser Call Active</p>
          </div>
        </div>
        <span data-testid="cw-call-timer" className="text-xl font-mono tabular-nums text-emerald-900">{fmtDuration(durationSeconds)}</span>
      </div>

      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700 text-center">
        Stay on this window to keep the call connected. Closing or navigating away will end the call.
      </p>

      {/* Mute / Keypad / Hang Up */}
      <div className="flex items-stretch justify-center gap-3">
        <button
          type="button"
          data-testid="call-mute-button"
          onClick={() => (muted ? voice.unmute() : voice.mute())}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
          className={`flex flex-col items-center justify-center gap-1 w-20 py-3 rounded-xl border text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
            muted ? 'border-yellow-300 bg-yellow-50 text-yellow-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <span className="text-base">{muted ? '🔇' : '🎤'}</span>
          <span>{muted ? 'Muted' : 'Mute'}</span>
        </button>
        <button
          type="button"
          data-testid="call-keypad-toggle"
          aria-pressed={showKeypad}
          onClick={() => setShowKeypad((v) => !v)}
          aria-label={showKeypad ? 'Hide keypad' : 'Show keypad'}
          className={`flex flex-col items-center justify-center gap-1 w-20 py-3 rounded-xl border text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
            showKeypad ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <span className="text-base">⌨️</span>
          <span>Keypad</span>
        </button>
        <button
          type="button"
          data-testid="call-end-button"
          onClick={voice.endCall}
          aria-label="Hang up"
          className="flex flex-col items-center justify-center gap-1 w-20 py-3 rounded-xl bg-red-600 text-white text-xs font-medium hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
        >
          <span className="text-base">📵</span>
          <span>Hang Up</span>
        </button>
      </div>

      {/* Active-call recording card. Renders only when the call is
          connected/answered. Backend is the source of truth (via
          `/recording/effective-state`) for status + policy. Manual
          default → Start Recording button; Automatic default →
          auto-records on connect; Compliance-locked → status-only. */}
      <ActiveCallRecordingCard
        callSid={twilioCallSid}
        workspaceRecordingMode={workspaceRecordingMode}
        activeCallKind={call.kind}
        outboundCallId={outboundCallId}
      />

      {/* DTMF keypad (collapsible) */}
      {showKeypad && (
        <div data-testid="call-keypad" className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] text-slate-500 mb-2 text-center">Press digits during the call</p>
          <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto">
            {DTMF_KEYS.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`call-dtmf-${DTMF_TESTID[d] ?? d}`}
                onClick={() => voice.sendDigits(d)}
                aria-label={`Send DTMF ${d}`}
                className="py-2.5 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 active:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Speaker / Mic selectors */}
      {(showSpeaker || showMic) && (
        <div data-testid="cw-call-audio" className="space-y-2">
          {showSpeaker && (
            <label className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500 w-16">Speaker:</span>
              <select
                data-testid="call-speaker-select"
                value={audio.selectedOutputId ?? ''}
                onChange={(e) => void voice.setOutputDevice(e.target.value)}
                aria-label="Select speaker"
                className="flex-1 text-xs px-2 py-1.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                {audio.outputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                ))}
              </select>
            </label>
          )}
          {showMic && (
            <label className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500 w-16">Mic:</span>
              <select
                data-testid="call-mic-select"
                value={audio.selectedInputId ?? ''}
                onChange={(e) => void voice.setInputDevice(e.target.value)}
                aria-label="Select microphone"
                className="flex-1 text-xs px-2 py-1.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                {audio.inputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </section>
  );
}

export default function ContactWorkspaceCallColumn({ crmContactId, workspaceData, workspaceStatus, voice }: Props) {
  const [notes, setNotes] = useState('');
  // Snapshot of the just-ended call so the drawer can show "Call ended • MM:SS"
  // while staying open (Decision 4 — no forced disposition, drawer persists).
  const [endedInfo, setEndedInfo] = useState<{ durationSeconds: number; failed: boolean } | null>(null);

  const [addNote, noteState] = useAddOutboundContactNoteMutation();
  // Saving a note WRITES to Outbound; the server requires contacts.update. A
  // read-only principal (viewer) does not get a notes field it cannot save.
  const { canRunOutboundContactActions } = useContactPermissions();

  const activeKind = voice?.activeCall.kind ?? 'idle';

  // Track the terminal call snapshot; clear it the moment a NEW call begins.
  useEffect(() => {
    if (activeKind === 'connecting' || activeKind === 'in_call') {
      setEndedInfo(null);
      return;
    }
    const call = voice?.activeCall;
    if (call && (call.kind === 'ended' || call.kind === 'failed')) {
      setEndedInfo({
        durationSeconds: call.kind === 'ended' ? call.durationSeconds : 0,
        failed: call.kind === 'failed',
      });
    }
  }, [activeKind, voice]);

  if (!crmContactId) {
    return (
      <div data-testid="cw-call-column-empty" className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
        Select a contact to see call details.
      </div>
    );
  }
  if (workspaceStatus === 'loading' || !workspaceData) {
    return (
      <div className="space-y-3">
        <div data-testid="cw-call-column-skeleton" className="rounded-xl border border-slate-200 bg-white p-4" aria-hidden="true">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-200 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-2/3 bg-slate-200 rounded animate-pulse" />
              <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const contact = workspaceData.contact.data;
  const name = contact?.fullName ?? 'Unnamed contact';
  const hasPhone = !!(contact?.phone && contact.phone.trim());
  const lastCallAt = workspaceData.callBrief.sourceCallCompletedAt ?? workspaceData.callHistory.items[0]?.at ?? null;
  const lastCallOutcome = workspaceData.callHistory.items[0]?.details.callOutcome ?? null;

  const sessionConnecting = activeKind === 'connecting';
  const sessionInCall = activeKind === 'in_call';
  const showEnded = !!endedInfo && !sessionConnecting && !sessionInCall;

  const trimmedNotes = notes.trim();
  const notesTooLong = trimmedNotes.length > OUTBOUND_NOTE_MAX;
  const canSaveNote = trimmedNotes.length > 0 && !notesTooLong && !noteState.isLoading;

  const noteFeedback: { kind: 'success' | 'error'; text: string } | null = noteState.isSuccess
    ? { kind: 'success', text: 'Note saved.' }
    : noteState.isError
      ? { kind: 'error', text: 'Could not save note.' }
      : null;

  const handleSaveNote = async () => {
    if (!canSaveNote) return;
    try {
      await addNote({ crmContactId, body: trimmedNotes }).unwrap();
      setNotes('');
    } catch {
      // error surfaces via noteFeedback
    }
  };

  return (
    <div className="space-y-3">
      {/* Call details card + Call button. Read-only history fields render ONLY
          when they have a value — no dangling "—" placeholders. */}
      <section data-testid="cw-call-details-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-500 mb-2">Call Details</p>
        {(lastCallOutcome || lastCallAt) ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {lastCallOutcome ? (
              <div>
                <Label text="Last outcome" />
                <p data-testid="cw-last-outcome" className="font-medium text-slate-900">
                  {formatOutcomeLabel(lastCallOutcome)}
                </p>
              </div>
            ) : null}
            {lastCallAt ? (
              <div>
                <Label text="Last call" />
                <p className="font-medium text-slate-900">{new Date(lastCallAt).toLocaleDateString()}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        {!hasPhone ? (
          <p
            data-testid="cw-no-phone"
            className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-center text-sm text-slate-500"
          >
            No phone number is available for this contact.
          </p>
        ) : voice ? (
          <CallButtonView
            crmContactId={crmContactId}
            contactName={contact?.fullName ?? null}
            contactPhone={contact?.phone ?? null}
            voice={voice}
          />
        ) : (
          <CallButtonSelfContained
            crmContactId={crmContactId}
            contactName={contact?.fullName ?? null}
            contactPhone={contact?.phone ?? null}
          />
        )}
      </section>

      {/* Ringing (connecting) — amber card + Cancel Call */}
      {voice && sessionConnecting && (
        <section data-testid="cw-call-ringing" className="rounded-xl border border-amber-200 bg-amber-50 shadow-sm p-6 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-amber-200 flex items-center justify-center mb-3">
            <div className="w-8 h-8 rounded-full bg-amber-400 animate-pulse" />
          </div>
          <p className="text-sm font-semibold text-amber-800">Ringing {name}…</p>
          <p className="text-xs text-amber-700 mt-0.5">Waiting for the lead to answer</p>
          <button type="button" data-testid="cw-call-cancel" onClick={voice.endCall} className="mt-4 px-5 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700">
            Cancel Call
          </button>
        </section>
      )}

      {/* Browser Call Active (in_call) — inline controls.
          OUTBOUND: render during any active call in an open column — UNCHANGED.
          INBOUND (additive): render only in the CALLER's own column (the
          Contact Workspace opened for the inbound caller), so the inbound call
          shows its controls here and nowhere else. `direction` is absent on
          every outgoing call, so the outbound branch is always taken for them
          and their behaviour is byte-identical. */}
      {voice &&
        sessionInCall &&
        (voice.activeCall.kind === 'in_call' &&
        (voice.activeCall.direction ?? 'outbound') === 'inbound'
          ? voice.activeCall.contactId === crmContactId
          : true) && <ActiveCallInline voice={voice} name={name} />}

      {/* Post-call — small, non-intrusive line; drawer STAYS open, no outcome. */}
      {showEnded && endedInfo && (
        <section data-testid="cw-call-ended" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-slate-400 flex-none" />
          <p className="text-xs font-medium text-slate-600">
            {endedInfo.failed ? 'Call ended' : `Call ended • ${fmtDuration(endedInfo.durationSeconds)}`}
          </p>
        </section>
      )}

      {/* Call Notes — ONE standalone field, always visible (before/during/after
          a call). Saved via the existing Add Note endpoint; not tied to any
          outcome or callback. */}
      {canRunOutboundContactActions && (
      <section data-testid="cw-call-notes-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-500">Call Notes</p>
        </div>
        <textarea
          data-testid="cw-call-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Take notes before, during, or after the call…"
          rows={4}
          disabled={noteState.isLoading}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm text-slate-800 p-3 resize-none disabled:opacity-70"
        />
        <div
          data-testid="cw-call-notes-counter"
          data-over={notesTooLong ? 'true' : 'false'}
          className={notesTooLong ? 'mt-1 text-right text-[11px] text-red-700 font-medium' : 'mt-1 text-right text-[11px] text-slate-400'}
        >
          {trimmedNotes.length} / {OUTBOUND_NOTE_MAX}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          {noteFeedback ? (
            <p
              data-testid="cw-note-feedback"
              data-kind={noteFeedback.kind}
              className={noteFeedback.kind === 'error' ? 'text-xs text-red-700' : 'text-xs text-emerald-700'}
            >
              {noteFeedback.text}
            </p>
          ) : (
            <span />
          )}
          <button
            type="button"
            data-testid="cw-note-save"
            onClick={() => void handleSaveNote()}
            disabled={!canSaveNote}
            className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {noteState.isLoading ? (
              <>
                <span aria-hidden className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Saving…
              </>
            ) : (
              'Save note'
            )}
          </button>
        </div>
      </section>
      )}
    </div>
  );
}
